import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl, fmtMoney } from "@/lib/api";
import { toast } from "sonner";
import {
  Upload,
  Sparkles,
  X,
  Plus,
  Wand2,
  Save,
  ImageIcon,
  RefreshCw,
} from "lucide-react";

const CATEGORIES = [
  "Recliners",
  "Chairs",
  "Furniture",
  "Mattresses",
  "Scooters",
  "E-Bikes",
  "Appliances",
  "Landscaping",
  "Closeout & Overstock Pallets",
  "Deals Under $20",
];

export default function AdminQuickAdd() {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]); // File[]
  const [previews, setPreviews] = useState([]); // local URLs
  const [caption, setCaption] = useState("");
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [parsed, setParsed] = useState(null); // {parsed, image_paths}
  const [stock, setStock] = useState(1);
  const dropRef = useRef(null);

  const addFiles = (incoming) => {
    const arr = Array.from(incoming || []).filter((f) =>
      f.type.startsWith("image/")
    );
    const next = [...files, ...arr].slice(0, 6);
    setFiles(next);
    setPreviews(next.map((f) => URL.createObjectURL(f)));
  };

  const onFileInput = (e) => addFiles(e.target.files);

  const onPaste = (e) => {
    const items = e.clipboardData?.items || [];
    const imgs = [];
    for (const it of items) {
      if (it.type.startsWith("image/")) {
        const f = it.getAsFile();
        if (f) imgs.push(f);
      }
    }
    if (imgs.length) {
      e.preventDefault();
      addFiles(imgs);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    dropRef.current?.classList.remove("border-[#0A1C40]", "bg-blue-50/40");
    if (e.dataTransfer?.files) addFiles(e.dataTransfer.files);
  };

  const removeFile = (idx) => {
    const next = files.filter((_, i) => i !== idx);
    setFiles(next);
    setPreviews(next.map((f) => URL.createObjectURL(f)));
  };

  const resetForm = () => {
    setFiles([]);
    setPreviews([]);
    setCaption("");
    setParsed(null);
    setStock(1);
  };

  const onParse = async () => {
    if (files.length === 0) {
      toast.error("Drop at least one image");
      return;
    }
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("caption", caption);
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/products/admin/quick-add/parse", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setParsed(data);
      // Pre-fill stock from caption (e.g. "qty 5" or "have 3" → 5 or 3)
      if (data?.parsed?.quantity && Number(data.parsed.quantity) > 0) {
        setStock(Number(data.parsed.quantity));
      }
      toast.success("AI extracted the product — review and save");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI parse failed");
    } finally {
      setParsing(false);
    }
  };

  const updateParsed = (patch) =>
    setParsed((p) => ({ ...p, parsed: { ...p.parsed, ...patch } }));

  const onSave = async (alsoNext) => {
    if (!parsed) return;
    setSaving(true);
    try {
      const p = parsed.parsed;
      const payload = {
        name: p.name,
        sku: p.suggested_sku || undefined,
        description: p.description || "",
        category: p.category,
        price: parseFloat(p.price) || 0,
        stock: parseInt(stock) || 0,
        is_pallet: Boolean(p.is_pallet),
        is_active: true,
        images: parsed.image_paths,
        tags: p.tags || [],
        low_stock_threshold: 2,
      };
      await api.post("/products/admin", payload);
      toast.success(`Saved: ${p.name}`);
      if (alsoNext) {
        resetForm();
      } else {
        navigate("/admin/products");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-quick-add" onPaste={onPaste}>
      <div className="ipv-eyebrow text-[#0A1C40] flex items-center gap-2">
        <Sparkles className="w-3.5 h-3.5" /> AI-powered
      </div>
      <h1 className="font-display font-black text-3xl tracking-tighter mt-2">
        Quick Add from WhatsApp
      </h1>
      <p className="text-gray-600 mt-2 max-w-2xl">
        Drop product photos (or paste from clipboard), paste the price/caption
        from WhatsApp, and AI will name, describe, categorize, and price the item
        for you. Review and save in seconds.
      </p>

      <div className="mt-8 grid lg:grid-cols-[1fr_460px] gap-8">
        {/* LEFT: input */}
        <div className="space-y-5">
          <div
            ref={dropRef}
            onDragOver={(e) => {
              e.preventDefault();
              dropRef.current?.classList.add("border-[#0A1C40]", "bg-blue-50/40");
            }}
            onDragLeave={() =>
              dropRef.current?.classList.remove("border-[#0A1C40]", "bg-blue-50/40")
            }
            onDrop={onDrop}
            className="bg-white border-2 border-dashed border-gray-300 p-8 text-center transition-colors"
            data-testid="quick-add-dropzone"
          >
            {previews.length === 0 ? (
              <>
                <ImageIcon className="w-10 h-10 mx-auto text-gray-300" />
                <div className="font-display font-bold text-lg mt-4">
                  Drop photos here
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Drag-and-drop, paste from clipboard (Ctrl/Cmd+V), or click to
                  browse — up to 6 images per item.
                </p>
                <label className="btn-primary inline-flex items-center gap-2 mt-5 cursor-pointer" data-testid="quick-add-browse-label">
                  <Upload className="w-4 h-4" /> Browse photos
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={onFileInput}
                    data-testid="quick-add-file-input"
                  />
                </label>
              </>
            ) : (
              <>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                  {previews.map((url, i) => (
                    <div key={i} className="relative aspect-square border border-gray-200 bg-gray-50">
                      <img
                        src={url}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                      <button
                        onClick={() => removeFile(i)}
                        className="absolute top-1 right-1 bg-black/70 text-white p-1 hover:bg-[#DC2626]"
                        data-testid={`quick-add-remove-${i}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {files.length < 6 && (
                    <label className="aspect-square border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-[#0A1C40] hover:text-[#0A1C40] text-gray-400">
                      <Plus className="w-6 h-6" />
                      <input type="file" accept="image/*" multiple className="hidden" onChange={onFileInput} />
                    </label>
                  )}
                </div>
                <div className="mt-4 text-xs text-gray-500">
                  {files.length} of 6 photos · drag to add more
                </div>
              </>
            )}
          </div>

          <div>
            <label className="ipv-eyebrow mb-2 block">
              WhatsApp caption / price text
            </label>
            <textarea
              className="input-base"
              rows={4}
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder={'e.g. "#ebike $999 qty 5"  ·  "#fridge $700 have 3"  ·  "Power recliner $350 stock 2"'}
              data-testid="quick-add-caption"
            />
            <div className="text-xs text-gray-600 mt-2 space-y-1" data-testid="quick-add-caption-help">
              <p>
                <span className="font-bold">💰 Price</span> — &quot;$999&quot;, &quot;199.99&quot;, &quot;125 each&quot;.{" "}
                <span className="text-[#0A1C40] font-bold">Your caption price always wins</span> — any Walmart/Amazon sticker price visible in the photo is ignored.
              </p>
              <p>
                <span className="font-bold">📦 Stock</span> — &quot;qty 5&quot;, &quot;have 10&quot;, &quot;stock 3&quot;, &quot;5x&quot;, &quot;5 each&quot;, &quot;10 items&quot;.
              </p>
              <p>
                <span className="font-bold">📁 Category</span> — add a hashtag to force the right section:{" "}
                <code className="text-[10px] bg-gray-100 px-1.5 py-0.5">#ebike #scooter #mattress #fridge #recliner #chair #sofa #mower #washer #dryer #pallet</code>
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={onParse}
              disabled={parsing || files.length === 0}
              className="btn-primary inline-flex items-center gap-2 flex-1 justify-center"
              data-testid="quick-add-parse-btn"
            >
              {parsing ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  AI is analyzing…
                </>
              ) : (
                <>
                  <Wand2 className="w-4 h-4" />
                  Parse with AI
                </>
              )}
            </button>
            {(files.length > 0 || parsed) && (
              <button onClick={resetForm} className="btn-outline" data-testid="quick-add-reset-btn">
                Reset
              </button>
            )}
          </div>
        </div>

        {/* RIGHT: preview/edit */}
        <aside className="space-y-4">
          {!parsed ? (
            <div className="bg-white border border-dashed border-gray-300 p-10 text-center">
              <Sparkles className="w-8 h-8 mx-auto text-gray-300" />
              <div className="font-display font-bold mt-4">
                AI preview will appear here
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Drop photos, hit Parse, and the AI-extracted product will appear
                here. Tweak anything, then save.
              </p>
            </div>
          ) : (
            <div className="bg-white border border-gray-200 p-5 space-y-4" data-testid="quick-add-preview">
              {parsed.image_paths?.[0] && (
                <div className="aspect-[4/3] -mx-5 -mt-5 mb-2 overflow-hidden bg-gray-100">
                  <img
                    src={fileUrl(parsed.image_paths[0])}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              <div className="flex items-start justify-between gap-2">
                <div className="ipv-eyebrow flex items-center gap-1.5 text-[#0A1C40]">
                  <Sparkles className="w-3 h-3" /> AI extracted
                </div>
                {parsed.parsed.confidence !== undefined && (
                  <span className="text-xs text-gray-500">
                    {Math.round((parsed.parsed.confidence || 0) * 100)}% confidence
                  </span>
                )}
              </div>

              <div>
                <label className="ipv-eyebrow block mb-1">Name</label>
                <input
                  className="input-base"
                  value={parsed.parsed.name || ""}
                  onChange={(e) => updateParsed({ name: e.target.value })}
                  data-testid="qa-name"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="ipv-eyebrow block mb-1">Category</label>
                  <select
                    className="input-base"
                    value={parsed.parsed.category || "Furniture"}
                    onChange={(e) => updateParsed({ category: e.target.value })}
                    data-testid="qa-category"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="ipv-eyebrow block mb-1">Price ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="input-base"
                    value={parsed.parsed.price ?? 0}
                    onChange={(e) =>
                      updateParsed({ price: parseFloat(e.target.value) || 0 })
                    }
                    data-testid="qa-price"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="ipv-eyebrow block mb-1">SKU</label>
                  <input
                    className="input-base"
                    value={parsed.parsed.suggested_sku || ""}
                    onChange={(e) => updateParsed({ suggested_sku: e.target.value })}
                    data-testid="qa-sku"
                  />
                </div>
                <div>
                  <label className="ipv-eyebrow block mb-1">Stock</label>
                  <input
                    type="number"
                    className="input-base"
                    value={stock}
                    onChange={(e) => setStock(e.target.value)}
                    data-testid="qa-stock"
                  />
                </div>
              </div>

              <div>
                <label className="ipv-eyebrow block mb-1">Description</label>
                <textarea
                  rows={3}
                  className="input-base"
                  value={parsed.parsed.description || ""}
                  onChange={(e) => updateParsed({ description: e.target.value })}
                  data-testid="qa-description"
                />
              </div>

              <div className="flex items-center gap-2 text-sm pt-2">
                <input
                  type="checkbox"
                  checked={Boolean(parsed.parsed.is_pallet)}
                  onChange={(e) => updateParsed({ is_pallet: e.target.checked })}
                  data-testid="qa-is-pallet"
                />
                <span>Wholesale / closeout / overstock pallet</span>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 pt-4 border-t border-gray-200">
                <button
                  onClick={() => onSave(true)}
                  disabled={saving}
                  className="btn-primary inline-flex items-center justify-center gap-2 flex-1"
                  data-testid="qa-save-next-btn"
                >
                  <Save className="w-4 h-4" />
                  Save & next item
                </button>
                <button
                  onClick={() => onSave(false)}
                  disabled={saving}
                  className="btn-outline inline-flex items-center justify-center gap-2"
                  data-testid="qa-save-done-btn"
                >
                  Save & view catalog
                </button>
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
